import { i18n } from 'next-i18next'
import { Schema, boolean, date, object, string } from 'yup'
import { Config } from 'modules/config/state/config-types'
import { OrderCustomAnswersSlice } from './custom-answers-types'
import { Question } from 'models/Question'
import { formatAnswerDate } from 'utils/functions/dateUtils'
import { FIELD_CHARACTER_LIMITS } from 'utils/constants/misc'

export const makeOrderCustomAnswersInitialState = (config: Config): OrderCustomAnswersSlice => {
  return config.questions.custom.reduce((acc, cur) => {
    const answerId = `${cur.id}`

    acc[answerId] = undefined

    return acc
  }, {} as OrderCustomAnswersSlice)
}

export const makeOrderCustomAnswersValidationSchema = (config: Config) => {
  const schemaFields = config.questions.custom.reduce((acc, cur) => {
    const key = `${cur.id}`

    const questionSchema = makeOrderCustomQuestionSchema(cur)

    if (questionSchema) {
      acc[key] = questionSchema
    }

    return acc
  }, {} as Record<string, Schema>)

  return object(schemaFields)
}

export const makeOrderCustomAnswerValidationSchema = (config: Config, questionId: number) => {
  const question = config.questions.custom.find((question) => question.id === questionId)

  if (!question) {
    return undefined
  }

  const questionSchema = makeOrderCustomQuestionSchema(question)

  if (question && questionSchema) {
    return object({ [question.id]: questionSchema })
  }

  return undefined
}

const makeOrderCustomQuestionSchema = (question: Question): Schema | undefined => {
  const t = i18n?.t.bind(i18n) || ((key: string, fallback: string) => fallback)

  const REQUIRED_MESSAGE = t('CustomAnswers.validation.fieldRequired', 'Enter a valid answer')
  const MAX_LENGTH_MESSAGE = t('CustomAnswers.validation.limitExceeded', 'You are over the limit')

  const VALUE_NUMBER_MESSAGE = t('CustomAnswers.validation.invalidNumber', 'Enter a valid number')

  const lenTestFn = (value?: string) =>
    value === undefined ? true : value.length <= FIELD_CHARACTER_LIMITS.CUSTOM_STRING

  const requiredTestFn = (value?: string | boolean | number | Date) =>
    question.is_required ? !!value : true

  switch (question.type) {
    case 'string':
    case 'text':
      return string()
        .test('required', REQUIRED_MESSAGE, requiredTestFn)
        .test(
          'len',
          t('validation.truncation.customField', 'This field must be 255 characters or less'),
          lenTestFn,
        )

    case 'integer':
      return string()
        .test('required', REQUIRED_MESSAGE, requiredTestFn)
        .test(
          'len',
          t('validation.truncation.customField', 'This field must be 255 characters or less'),
          lenTestFn,
        )
        .test('validNumber', VALUE_NUMBER_MESSAGE, (value) => {
          if (value === undefined) {
            return true
          }

          return !isNaN(Number(value))
        })
        .test('nonExponential', VALUE_NUMBER_MESSAGE, (value) => {
          if (value === undefined) {
            return true
          }

          return !String(value).includes('e')
        })

    case 'boolean':
      return boolean().test('required', REQUIRED_MESSAGE, requiredTestFn)

    case 'currency':
      return string()
        .test('required', REQUIRED_MESSAGE, requiredTestFn)
        .test(
          'validString',
          t('CustomAnswers.validation.invalidDollarAmount', 'Enter a valid amount'),
          (value) => {
            if (value === undefined || value === null || value === '') {
              return true
            }

            // Regex expression to the the input of a dollar amount including commas and decimals
            const regexp = /^(?:\$)?(?!.*[.,]{2})(\d{1,3}(?:,\d{3})*|\d+)(\.\d{1,2})?$/

            return regexp.test(value as string)
          },
        )

    case 'date':
      return date()
        .test(
          'required',
          t('CustomAnswers.validation.invalidDate', 'Enter a valid date'),
          requiredTestFn,
        )
        .transform((cur, orig) => (orig === '' || orig === null ? undefined : cur))

    case 'yes_no':
      return string().test(
        'required',
        t('CustomAnswers.validation.yesNoRequired', 'Choose yes or no'),
        (val) => (question.is_required ? !!val : true),
      )

    case 'enum':
      return string().test('required', REQUIRED_MESSAGE, requiredTestFn)

    default:
      console.warn(`Unsupported type: ${question.type}`)
      return undefined
  }
}

export const makeOrderCustomAnswersPayload = (config: Config, value: OrderCustomAnswersSlice) => {
  return config.questions.custom
    .map((question) => ({
      question_id: question.id,
      answer: value[question.id] as string,
    }))
    .filter((answer) => !!answer.answer)
}

export const parseRawOrderCustomAnswers = (config: Config, rawValue: Record<string, string>) => {
  const value = config.questions.custom.reduce((acc, cur) => {
    const answerId = `${cur.id}`
    const answerValue = rawValue[answerId]

    if (typeof answerValue === 'undefined') {
      return acc
    }

    switch (cur.type) {
      case 'date':
        acc[answerId] = formatAnswerDate(answerValue)

        break
      case 'currency':
        acc[answerId] = Number(`${answerValue}`.replace(/[^0-9.]/g, ''))

        break
      default:
        acc[answerId] = answerValue

        break
    }

    return acc
  }, {} as OrderCustomAnswersSlice)

  return value
}
